import React, { useCallback, useRef, useState } from "react";
import TextArea from "antd/lib/input/TextArea";
import { EditOutlined, HighlightOutlined } from "@ant-design/icons";
import { Button, Col, Drawer, Form, Input, Row, Select, Spin, Card, Typography, Modal, InputNumber } from "antd";
import { connect } from "dva";
import { QuoteFeedbackType, QuoteFeedbackTypeMap } from "@/utils/enum";
import { adjustPriceQuotes } from "@/services/quotes_new";
import useScreenDetector from "@/hooks/useScreenDetector";
import AlertWithBorder from "../AlertWithBorder";
import MarginFeedbackAlert from "../MarginFeedbackAlert";
import styles from "./VehicleAdjustQuote.styles.less";

const { Text } = Typography;

function VehicleAdjustQuote({ alertMessage, alertType, quoteData, shipmentData, onNotesUpdate, onQuoteUpdate }) {
  const { isMobile } = useScreenDetector();
  const adjustQuoteFromRef = useRef();
  const lowPriceMarginFormRef = useRef();

  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dealerPrice, setDealerPrice] = useState(quoteData?.dealerPrice || 0);
  const [transporterPrice, setTransporterPrice] = useState(quoteData?.transporterPrice || 0);
  const [adjustingDealerPrice, setAdjustingDealerPrice] = useState(false);
  const [adjustingTransporterPrice, setAdjustingTransporterPrice] = useState(false);
  const [dealerFeedbackType, setDealerFeedbackType] = useState(null);
  const [transporterFeedbackType, setTransporterFeedbackType] = useState(null);

  const [displayLowPriceMarginModal, setDisplayLowPriceMarginModal] = useState(false);

  const onClose = useCallback(() => {
    setVisible(false);
    setDisplayLowPriceMarginModal(false);
    adjustQuoteFromRef.current?.resetFields();
    lowPriceMarginFormRef.current?.resetFields();
  }, []);

  const onSubmit = useCallback(
    validated => {
      adjustQuoteFromRef.current.validateFields().then(async result => {
        const margin = Math.abs((transporterPrice - dealerPrice) / dealerPrice) * 100;
        const isLowMargin = margin < 15 || transporterPrice >= dealerPrice;

        let lowPriceMarginResult = null;

        if (isLowMargin && !validated) {
          setDisplayLowPriceMarginModal(true);
          return;
        }
        if (isLowMargin) {
          try {
            lowPriceMarginResult = await lowPriceMarginFormRef.current.validateFields();
          } catch {
            return;
          }
        }

        const adjustPayload = {
          ...result,
          lowMarginReason: lowPriceMarginResult?.lowMarginReason,
          note: "",
          shipmentId: shipmentData.id,
        };

        setLoading(true);
        adjustPriceQuotes(quoteData.identifier, adjustPayload)
          .then(response => {
            if (typeof onNotesUpdate === "function") {
              onNotesUpdate();
            }

            if (typeof onQuoteUpdate === "function") {
              onQuoteUpdate(response);
            }

            onClose();
          })
          .finally(() => {
            setLoading(false);
          });
      });
    },
    [quoteData, shipmentData, dealerPrice, transporterPrice, onNotesUpdate, onQuoteUpdate, onClose],
  );

  return (
    <div style={{ width: "100%" }}>
      <div className={styles.adjustPriceWrapper}>
        <Button onClick={() => setVisible(true)} icon={<EditOutlined />} className={styles.adjustPriceButton}>
          Adjust Prices
        </Button>
      </div>

      <Drawer
        title="Adjust Quote"
        width={isMobile ? "80%" : "60%"}
        onClose={onClose}
        open={visible}
        bodyStyle={{ paddingBottom: 80 }}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <MarginFeedbackAlert dealerPrice={Number(dealerPrice)} transporterPrice={Number(transporterPrice)} />
            <div style={{ textAlign: "right" }}>
              <Button onClick={onClose} style={{ marginRight: 8 }}>
                Cancel
              </Button>
              <Button type="primary" onClick={() => onSubmit(false)}>
                Submit
              </Button>
            </div>
          </div>
        }
      >
        <Spin spinning={loading}>
          <AlertWithBorder
            message={alertMessage}
            type={alertType}
            actionButtons={[
              {
                label: "Review",
                key: "review-pricing-engine-results",
                href: `/admin/shipments/view/messages?id=${quoteData?.id}&tab=${alertType}&shipmentId=${shipmentData?.id}`,
              },
            ]}
          />
          <Form ref={adjustQuoteFromRef} layout="vertical">
            <div style={{ gap: 16, display: "flex", flexDirection: "column" }}>
              <Col>
                <Card>
                  <Col>
                    <Row style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Text style={{ fontWeight: "normal", fontSize: "12px" }}>Suggested Dealer Price</Text>
                        <Text strong style={{ fontWeight: "normal", fontSize: "32px" }}>
                          {`$${quoteData?.dealerPrice}`}
                        </Text>
                      </div>
                      <Button
                        icon={adjustingDealerPrice ? <HighlightOutlined /> : <EditOutlined />}
                        onClick={() => {
                          setAdjustingDealerPrice(!adjustingDealerPrice);
                          if (adjustingDealerPrice) {
                            adjustQuoteFromRef.current?.setFieldsValue({
                              dealerPrice: null,
                              dealerPriceType: null,
                            });
                          }
                        }}
                      >
                        {adjustingDealerPrice ? "Clear" : "Adjust Prices"}
                      </Button>
                    </Row>
                    {adjustingDealerPrice && (
                      <>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="dealerPrice" label="Dealer Price" rules={[{ required: true }]}>
                              <InputNumber onChange={setDealerPrice} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="dealerPriceType"
                              label="Dealer Estimate Feedback"
                              rules={[{ required: true }]}
                            >
                              <Select onChange={setDealerFeedbackType}>
                                {Object.keys(QuoteFeedbackTypeMap).map(key => (
                                  <Select.Option key={key} value={parseInt(key, 10)}>
                                    {QuoteFeedbackTypeMap[key]}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Col span={24} hidden={dealerFeedbackType !== QuoteFeedbackType.Other}>
                          <Form.Item
                            name="dealerOtherNote"
                            label="Notes"
                            rules={[
                              {
                                required: dealerFeedbackType === QuoteFeedbackType.Other,
                              },
                            ]}
                          >
                            <Input.TextArea rows={4} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Col>
                </Card>
              </Col>
              <Col>
                <Card>
                  <Col>
                    <Row style={{ justifyContent: "space-between" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <Text style={{ fontWeight: "normal", fontSize: "12px" }}>Suggested Transporter Price</Text>
                        <Text strong style={{ fontWeight: "normal", fontSize: "32px" }}>
                          {`$${quoteData?.transporterPrice}`}
                        </Text>
                      </div>
                      <Button
                        icon={adjustingTransporterPrice ? <HighlightOutlined /> : <EditOutlined />}
                        onClick={() => {
                          setAdjustingTransporterPrice(!adjustingTransporterPrice);

                          if (adjustingTransporterPrice) {
                            adjustQuoteFromRef.current?.setFieldsValue({
                              transporterPrice: null,
                              transporterPriceType: null,
                            });
                          }
                        }}
                      >
                        {adjustingTransporterPrice ? "Clear" : "Adjust Prices"}
                      </Button>
                    </Row>
                    {adjustingTransporterPrice && (
                      <>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item name="transporterPrice" label="Transporter Price" rules={[{ required: true }]}>
                              <InputNumber onChange={setTransporterPrice} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="transporterPriceType"
                              label="Transporter Estimate Feedback"
                              rules={[{ required: true }]}
                            >
                              <Select onChange={setTransporterFeedbackType}>
                                {Object.keys(QuoteFeedbackTypeMap).map(key => (
                                  <Select.Option key={key} value={parseInt(key, 10)}>
                                    {QuoteFeedbackTypeMap[key]}
                                  </Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                        </Row>
                        <Col span={24} hidden={transporterFeedbackType !== QuoteFeedbackType.Other}>
                          <Form.Item
                            name="transporterOtherNote"
                            label="Notes"
                            rules={[
                              {
                                required: transporterFeedbackType === QuoteFeedbackType.Other,
                              },
                            ]}
                          >
                            <Input.TextArea rows={4} />
                          </Form.Item>
                        </Col>
                      </>
                    )}
                  </Col>
                </Card>
              </Col>
            </div>
          </Form>
        </Spin>
      </Drawer>
      <Modal
        title="Low Pricing Margin"
        open={displayLowPriceMarginModal}
        onOk={() => onSubmit(true)}
        onCancel={() => setDisplayLowPriceMarginModal(false)}
        confirmLoading={loading}
        afterClose={() => {
          lowPriceMarginFormRef?.current.resetFields();
        }}
      >
        <div style={{ marginBottom: 32 }}>Pricing margin is less than 15%. Please enter reason for low margin.</div>
        <Form ref={lowPriceMarginFormRef} layout="vertical">
          <Form.Item name="lowMarginReason" label="Reason for Low Margin" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default connect(({ adminShipmentView }) => ({
  shipmentData: adminShipmentView.data,
}))(VehicleAdjustQuote);
